import { useState, useEffect } from 'react'
import { useWizardStore } from '../../store/wizardStore'
import { Button } from '../ui/Button'
import { CardHeader, CardTitle, CardContent } from '../ui/Card'
import { api } from '../../lib/api'
import type { TimeSlot } from '../../lib/types'
import { Modal } from '../ui/Modal'
import { clsx } from 'clsx'

type Slot = Pick<TimeSlot, 'id' | 'time' | 'slotNumber' | 'status'>

export default function Step5SlotSelection() {
  const { nextStep, previousStep, wantsGifts, setStep, setSelectedTimeSlotId, selectedTimeSlotId, orderId, setSlotHeldUntil, slotExpired, clearSlot, setWantsSanta } = useWizardStore()
  const [slots, setSlots] = useState<Slot[]>([])
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(
    slotExpired ? 'Your reservation expired. Please choose a slot again.' : null
  )
  const [confirmRelease, setConfirmRelease] = useState(false)
  const [isReleasing, setIsReleasing] = useState(false)
  const [released, setReleased] = useState(false)

  useEffect(() => {
    // Restore previously selected slot when navigating back
    if (selectedTimeSlotId) {
      setSelectedSlot(selectedTimeSlotId)
    }
    loadSlots()
  }, [selectedTimeSlotId])

  const loadSlots = async () => {
    setIsLoading(true)
    try {
      setSlots(await api.getSlots())
    } catch (err) {
      setError('Failed to load available slots. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Free slots, plus the one this order already holds (it shows as locked to everyone else)
  const isSelectable = (slot: Slot) => slot.status === 'available' || slot.id === selectedTimeSlotId
  const anyFree = slots.some(isSelectable)

  // Group slots by time
  const slotsByTime = slots.reduce((acc, slot) => {
    if (!acc[slot.time]) {
      acc[slot.time] = []
    }
    acc[slot.time].push(slot)
    return acc
  }, {} as Record<string, Slot[]>)

  const handleSlotSelect = (slotId: string) => {
    setSelectedSlot(slotId)
    setError(null)
  }

  const handleNext = async () => {
    if (!selectedSlot || !orderId) {
      setError('Please select a time slot')
      return
    }

    try {
      // If user is changing slots, release the old one first
      if (selectedTimeSlotId && selectedTimeSlotId !== selectedSlot && orderId) {
        try {
          await api.releaseSlot(selectedTimeSlotId, orderId)
        } catch (err) {
          console.log('Could not release old slot:', err)
          // Continue anyway - old slot might have expired already
        }
      }

      const locked = await api.lockSlot(selectedSlot, orderId)
      setSelectedTimeSlotId(selectedSlot)
      setSlotHeldUntil(locked.lockedUntil)
      nextStep()
    } catch (err) {
      setError('This slot is no longer available. Please select another.')
      setSelectedSlot(null)
      loadSlots() // Refresh slots
    }
  }

  const heldSlot = slots.find((s) => s.id === selectedTimeSlotId)

  // Give the held slot back straight away so another family can book it
  const handleRelease = async () => {
    if (!selectedTimeSlotId || !orderId) return
    setIsReleasing(true)
    try {
      await api.releaseSlot(selectedTimeSlotId, orderId)
    } catch (err: any) {
      // 409: this order no longer holds it (hold already expired), so it's free either way
      if (err.status !== 409) {
        setError("We couldn't release your slot. Please try again.")
        setIsReleasing(false)
        setConfirmRelease(false)
        return
      }
    }
    clearSlot()
    setSelectedSlot(null)
    setError(null)
    setReleased(true)
    setIsReleasing(false)
    setConfirmRelease(false)
    // The button that opened the modal is gone; return focus to the step title
    requestAnimationFrame(() => document.querySelector<HTMLElement>('main h2')?.focus())
  }

  const handleContinueWithoutSanta = () => {
    setWantsSanta(false)
    setStep(7)
  }

  const handleBack = () => {
    if (wantsGifts) {
      previousStep() // Back to Step 4
    } else {
      setStep(2) // Back to Step 2 (Gift Decision)
    }
  }

  return (
    <>
      <CardHeader>
        <CardTitle>Choose Your Santa Visit Time</CardTitle>
        <p className="text-gray-600 mt-2">
          Select an available time slot to visit Santa in his grotto.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {isLoading && (
            <div className="text-center py-8">
              <p role="status" className="text-gray-600">Loading available slots...</p>
            </div>
          )}

          {error && (
            <div role="alert" className="bg-primary-50 border border-primary-300 p-4 rounded-lg">
              <p className="text-primary-800">{error}</p>
            </div>
          )}

          {released && (
            <div role="status" className="bg-green-50 border border-green-200 p-4 rounded-lg">
              <p className="text-green-800">Your Santa slot has been released. Choose another, or continue without a Santa visit.</p>
            </div>
          )}

          {!isLoading && !anyFree && (
            <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg text-center">
              <p className="text-amber-800 font-medium">
                Sorry, all time slots are currently booked.
              </p>
            </div>
          )}

          {!isLoading && Object.keys(slotsByTime).length > 0 && (
            <fieldset className="space-y-3">
              <legend className="sr-only">Santa visit time</legend>
              {Object.entries(slotsByTime).map(([time, timeSlots]) => (
                <div key={time} className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">{time}</h3>
                  <div className="grid grid-cols-2 gap-2">
                    {timeSlots.map((slot) => {
                      const selectable = isSelectable(slot)
                      const selected = selectedSlot === slot.id
                      const isMine = slot.id === selectedTimeSlotId && slot.status !== 'available'
                      return (
                        <label
                          key={slot.id}
                          className={clsx(
                            'flex items-center gap-3 p-3 min-h-[56px] rounded-lg border-2 transition-colors motion-reduce:transition-none',
                            'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent-800 has-[:focus-visible]:ring-offset-2',
                            !selectable && 'border-dashed border-gray-200 bg-gray-50 cursor-not-allowed',
                            selectable && selected && 'border-accent-800 bg-accent-50 cursor-pointer',
                            selectable && !selected && 'border-gray-300 bg-white hover:border-accent-400 cursor-pointer'
                          )}
                        >
                          <input
                            type="radio"
                            name="santa-slot"
                            value={slot.id}
                            checked={selected}
                            disabled={!selectable}
                            onChange={() => handleSlotSelect(slot.id)}
                            className="sr-only"
                          />
                          {/* Large tick target; taken slots get none so they read as not-an-option */}
                          {selectable ? (
                            <span
                              aria-hidden="true"
                              className={clsx(
                                'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border-2',
                                selected ? 'border-accent-800 bg-accent-800 text-white' : 'border-gray-400 bg-white'
                              )}
                            >
                              {selected && (
                                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </span>
                          ) : (
                            <span aria-hidden="true" className="h-7 w-7 flex-shrink-0" />
                          )}
                          <span className="flex flex-col leading-tight">
                            <span className={clsx('font-medium', selectable ? 'text-gray-900' : 'text-gray-500 line-through')}>
                              Slot {slot.slotNumber}
                            </span>
                            <span className={clsx('text-xs', isMine ? 'text-green-800 font-medium' : 'text-gray-600')}>
                              {isMine ? 'Reserved for you' : selectable ? 'Available' : 'Booked'}
                            </span>
                          </span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              ))}
            </fieldset>
          )}

          <div className="pt-4 space-y-2">
            <Button
              onClick={handleNext}
              fullWidth
              size="lg"
              disabled={!selectedSlot || isLoading}
            >
              Continue
            </Button>
            {selectedTimeSlotId ? (
              <Button onClick={() => setConfirmRelease(true)} fullWidth variant="outline" disabled={isReleasing}>
                Remove my Santa slot
              </Button>
            ) : (
              <Button onClick={handleContinueWithoutSanta} fullWidth variant="outline" disabled={isLoading}>
                Continue without a Santa visit
              </Button>
            )}
            <Button
              onClick={handleBack}
              fullWidth
              variant="ghost"
            >
              Back
            </Button>
          </div>
        </div>
      </CardContent>

      <Modal
        isOpen={confirmRelease}
        onClose={isReleasing ? undefined : () => setConfirmRelease(false)}
        title="Remove your Santa slot?"
        icon="warning"
        actions={
          <div className="flex flex-col gap-2">
            <Button onClick={handleRelease} fullWidth variant="secondary" disabled={isReleasing}>
              {isReleasing ? 'Removing...' : 'Yes, remove my slot'}
            </Button>
            <Button onClick={() => setConfirmRelease(false)} fullWidth variant="ghost" disabled={isReleasing} autoFocus>
              Keep my slot
            </Button>
          </div>
        }
      >
        <p>
          {heldSlot ? `Slot ${heldSlot.slotNumber} at ${heldSlot.time}` : 'Your slot'} will be released straight away and another family may book it.
          Any children's names you entered for Santa will be removed too.
        </p>
      </Modal>
    </>
  )
}
